import React, { useState } from "react";
import axios from "axios";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 300;

export const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => {
    let v = e.target.value;
    if (k === "message" && v.length > MAX) v = v.slice(0, MAX);
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon. ✨");
      setSubmitted(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="w-full py-16 md:py-24 px-6"
      style={{ backgroundColor: "var(--bg-secondary)" }}
      data-testid="contact-section"
    >
      <div className="max-w-lg mx-auto text-center">
        <p
          className="font-script text-2xl md:text-3xl mb-2"
          style={{ color: "var(--text-accent)" }}
        >
          let’s chat
        </p>
        <h2
          className="font-display text-4xl md:text-5xl lg:text-6xl mb-3"
          style={{ color: "var(--text-primary)" }}
        >
          Contact Information
        </h2>
        <p
          className="text-base mb-10"
          style={{ color: "var(--text-secondary)" }}
        >
          Questions, or just want to say hi? We’d love to hear from you.
        </p>

        <form
          onSubmit={onSubmit}
          className="text-left space-y-4 bg-white p-6 md:p-8 rounded-2xl"
          style={{ border: "1px solid var(--border-soft)" }}
          data-testid="contact-form"
        >
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-semibold mb-1.5"
              style={{ color: "var(--text-primary)" }}
            >
              Name
            </label>
            <input
              id="name"
              type="text"
              required
              maxLength={120}
              value={form.name}
              onChange={update("name")}
              className="w-full rounded-xl px-4 py-2.5 outline-none transition-colors"
              style={{
                backgroundColor: "var(--bg-primary)",
                border: "1px solid var(--border-soft)",
                color: "var(--text-primary)",
              }}
              data-testid="contact-name-input"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold mb-1.5"
              style={{ color: "var(--text-primary)" }}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              className="w-full rounded-xl px-4 py-2.5 outline-none transition-colors"
              style={{
                backgroundColor: "var(--bg-primary)",
                border: "1px solid var(--border-soft)",
                color: "var(--text-primary)",
              }}
              data-testid="contact-email-input"
            />
          </div>

          <div>
            <label
              htmlFor="subject"
              className="block text-sm font-semibold mb-1.5"
              style={{ color: "var(--text-primary)" }}
            >
              Subject
            </label>
            <input
              id="subject"
              type="text"
              required
              maxLength={200}
              value={form.subject}
              onChange={update("subject")}
              className="w-full rounded-xl px-4 py-2.5 outline-none transition-colors"
              style={{
                backgroundColor: "var(--bg-primary)",
                border: "1px solid var(--border-soft)",
                color: "var(--text-primary)",
              }}
              data-testid="contact-subject-input"
            />
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-semibold mb-1.5"
              style={{ color: "var(--text-primary)" }}
            >
              Body Message
            </label>
            <textarea
              id="message"
              required
              rows={5}
              maxLength={MAX}
              value={form.message}
              onChange={update("message")}
              className="w-full rounded-xl px-4 py-2.5 outline-none resize-none transition-colors"
              style={{
                backgroundColor: "var(--bg-primary)",
                border: "1px solid var(--border-soft)",
                color: "var(--text-primary)",
              }}
              data-testid="contact-message-input"
            />
            <div
              className="text-right text-xs mt-1.5"
              style={{
                color:
                  form.message.length >= MAX
                    ? "var(--brand-orange)"
                    : "var(--text-secondary)",
              }}
              data-testid="char-counter"
            >
              {form.message.length} / {MAX}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-sage w-full mt-2 py-3 rounded-full text-sm font-semibold tracking-wide uppercase flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="contact-submit-button"
          >
            <Send size={16} />
            {submitting ? "Sending…" : "Send Message"}
          </button>

          {submitted && (
            <p
              className="text-center text-sm font-script text-xl pt-2"
              style={{ color: "var(--brand-sage)" }}
              data-testid="contact-success-message"
            >
              thank you — your message is on its way! ✿
            </p>
          )}
        </form>

        <div
          className="flex items-center justify-center gap-2 mt-6 text-sm"
          style={{ color: "var(--text-secondary)" }}
        >
          <Mail size={16} />
          <a
            href="mailto:shelbyjostovall04@gmail.com"
            className="hover:underline"
            data-testid="contact-email-link"
          >
            shelbyjostovall04@gmail.com
          </a>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
